import { Button, Col, Input, Row, Select } from "antd";
import { useEffect, useState } from "react";
import {
  DOMAIN_ID,
  ORG_ID,
  REACT_APP_API_TENANTS_URL,
} from "../../../../constants";
import axios from "axios";
const { Option } = Select;

export const SsoConfig = ({ message }) => {
  const [userRole, setUserRole] = useState("NORMAL");
  const [oktaSsoUrl, setOktaSsoUrl] = useState("");
  const [oktaMetadataUrl, setOktaMetadataUrl] = useState("");

  const [oneloginSsoUrl, setOneloginSsoUrl] = useState("");
  const [oneloginMetadataUrl, setOneloginMetadataUrl] = useState("");

  const [sloUrl, setSloUrl] = useState("");
  const [apiKey, setApiKey] = useState("");

  const [isSsoUrlEditable, setIsSsoUrlEditable] = useState(true);
  const [isSloUrlEditable, setIsSloUrlEditable] = useState(true);
  const [isMetadataUrlEditable, setIsMetadataUrlEditable] = useState(false);
  const [isApiKeyEditable, setIsApiKeyEditable] = useState(false);
  const [currentSavedAuthMethod, setCurrentSavedAuthMethod] = useState(null);

  const [hasConfigured, setHasConfigured] = useState({
    OKTA: false,
    ONELOGIN: false,
  });

  const [isEditMode, setIsEditMode] = useState({
    OKTA: false,
    ONELOGIN: false,
  });
  const [isSaveEnabled, setIsSaveEnabled] = useState({
    OKTA: false,
    ONELOGIN: false,
  });

  useEffect(() => {
    const fetchSSOConfiguration = async () => {
      try {
        const response = await axios.post(
          `${REACT_APP_API_TENANTS_URL}/getSSOConfiguration`,
          {
            orgId: ORG_ID(),
            tenantId: DOMAIN_ID(),
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data = response.data;
        // Set the saved auth method for comparison
        setCurrentSavedAuthMethod(data.authMethod);
        if (data.authMethod === "OKTA") {
          setUserRole("OKTA");

          setOktaSsoUrl(data.singleSignOnUrl || "");
          setOktaMetadataUrl(data.metadataUrl || "");
          setApiKey(data.apiKey || "");

          setHasConfigured((prev) => ({ ...prev, OKTA: true }));
          setIsEditMode((prev) => ({ ...prev, OKTA: true }));
          setIsSsoUrlEditable(false);
          setIsMetadataUrlEditable(false);
          setIsApiKeyEditable(false);
        }

        if (data.authMethod === "ONELOGIN") {
          setUserRole("ONELOGIN");

          setOneloginSsoUrl(data.singleSignOnUrl || "");
          setOneloginMetadataUrl(data.metadataUrl || "");
          setSloUrl(data.singleLogoutUrl || "");

          setHasConfigured((prev) => ({ ...prev, ONELOGIN: true }));
          setIsEditMode((prev) => ({ ...prev, ONELOGIN: true }));
          setIsSsoUrlEditable(false);
          setIsSloUrlEditable(false);
          setIsMetadataUrlEditable(false);
        } else if (data.authMethod === "NORMAL") {
          setUserRole("NORMAL");
        }
      } catch (error) {
        console.error("Failed to fetch SSO configuration:", error?.response?.status, error?.message);
      }
    };

    fetchSSOConfiguration();
  }, []);

  // When userRole changes, call save automatically if it's NORMAL
  useEffect(() => {
    const saveNormalConfig = async () => {
      if (
        userRole === "NORMAL" &&
        currentSavedAuthMethod !== "NORMAL" &&
        currentSavedAuthMethod !== null
      ) {
        const payload = {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
          authMethod: "NORMAL",
          singleSignOnUrl: "",
          singleLogoutUrl: "",
          metadataUrl: "",
          apiKey: "",
        };

        try {
          await axios.post(
            `${REACT_APP_API_TENANTS_URL}/setSSOConfiguration`,
            payload,
            {
              headers: { "Content-Type": "application/json" },
            },
          );
          setCurrentSavedAuthMethod("NORMAL");
        } catch (error) {
          console.error("Failed to save NORMAL config", error?.response?.status, error?.message);
        }
      }
    };

    saveNormalConfig();
  }, [userRole, currentSavedAuthMethod]);

  const handleUserRoleChange = (value) => {
    setUserRole(value); // This will trigger the useEffect for NORMAL auto-save

    // Only run the below logic for OKTA and ONELOGIN
    if (value === "OKTA" || value === "ONELOGIN") {
      const alreadyConfigured = hasConfigured[value];

      // Enable save button only if not configured yet
      setIsSaveEnabled((prev) => ({
        ...prev,
        [value]: !alreadyConfigured,
      }));

      // Set Edit Mode
      setIsEditMode((prev) => ({
        ...prev,
        [value]: alreadyConfigured,
      }));

      // Reset field edit states
      setIsSsoUrlEditable(false);
      setIsSloUrlEditable(false);
      setIsMetadataUrlEditable(false);
      setIsApiKeyEditable(false);

      // If it's not already configured, allow field editing
      if (!alreadyConfigured) {
        setIsSsoUrlEditable(true);
        setIsSloUrlEditable(true);
        setIsMetadataUrlEditable(true);
        setIsApiKeyEditable(true);
      }
    }
  };
  const handleSave = async () => {
    if (userRole === "OKTA" && (!oktaSsoUrl || !oktaMetadataUrl || !apiKey)) {
      alert("Please fill all OKTA fields");
      return;
    }
    if (
      userRole === "ONELOGIN" &&
      (!oneloginSsoUrl || !oneloginMetadataUrl || !sloUrl)
    ) {
      alert("Please fill all OneLogin fields");
      return;
    }
    // Update config state to show Edit option from next time
    setHasConfigured((prev) => ({ ...prev, [userRole]: true }));
    setIsEditMode((prev) => ({ ...prev, [userRole]: true }));

    let payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      authMethod: userRole,
      singleSignOnUrl: "",
      singleLogoutUrl: "",
      metadataUrl: "",
      apiKey: "",
    };
    if (userRole === "OKTA") {
      payload = {
        ...payload,
        singleSignOnUrl: oktaSsoUrl,
        metadataUrl: oktaMetadataUrl,
        apiKey: apiKey,
      };
    } else if (userRole === "ONELOGIN") {
      payload = {
        ...payload,
        singleSignOnUrl: oneloginSsoUrl,
        singleLogoutUrl: sloUrl,
        metadataUrl: oneloginMetadataUrl,
      };
    }

    try {
      await axios.post(
        `${REACT_APP_API_TENANTS_URL}/setSSOConfiguration`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );


      // Lock input fields again after saving
      setIsSsoUrlEditable(false);
      setIsSloUrlEditable(false);
      setIsMetadataUrlEditable(false);
      setIsApiKeyEditable(false);

      //  Make sure we're in view mode after save
      setIsEditMode((prev) => ({ ...prev, [userRole]: true }));
      setIsSaveEnabled((prev) => ({ ...prev, [userRole]: false }));

      //  Mark that this role has been configured
      setHasConfigured((prev) => ({ ...prev, [userRole]: true }));
      message.success("SSO configuration saved successfully!");
    } catch (error) {
      console.error("Error saving SSO configuration:", error?.response?.status, error?.message);
      message.error("Failed to save SSO configuration.");
    }
  };
  return (
    <>
      <Row gutter={16} style={{ marginLeft: "1rem" }}>
        <Col span={24}>
          <Row gutter={16}>
            <Col span={6}>
              <p className="search-label">Select SSO Configuration</p>
              <Select
                size="small"
                value={userRole}
                onChange={handleUserRoleChange}
                style={{ width: "100%" }}
              >
                <Option value="NORMAL">NORMAL</Option>
                <Option value="OKTA">OKTA</Option>
                <Option value="ONELOGIN">ONELOGIN</Option>
              </Select>
            </Col>
          </Row>

          {/* OKTA SECTION */}
          {userRole === "OKTA" && (
            <Row gutter={16} style={{ marginTop: "1rem" }}>
              <Col span={6}>
                <p className="search-label">SSO URL</p>
                <Input
                  size="small"
                  value={oktaSsoUrl}
                  onChange={(e) => setOktaSsoUrl(e.target.value)}
                  disabled={isEditMode[userRole] && !isSsoUrlEditable}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col
                span={2}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  padding: "0",
                  marginBottom: "10px",
                }}
              >
                {isEditMode.OKTA && !isSsoUrlEditable && (
                  <Button
                    size="small"
                    onClick={() => {
                      console.log("Edit clicked, userRole is:", userRole);
                      setIsSsoUrlEditable(true);
                      setIsSaveEnabled((prev) => ({
                        ...prev,
                        [userRole]: true,
                      }));
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Col>
              <Col span={6}>
                <p className="search-label">Metadata URL</p>
                <Input
                  size="small"
                  value={oktaMetadataUrl}
                  onChange={(e) => setOktaMetadataUrl(e.target.value)}
                  disabled={isEditMode[userRole] && !isMetadataUrlEditable}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col
                span={2}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  padding: "0",
                  marginBottom: "10px",
                }}
              >
                {isEditMode.OKTA && !isMetadataUrlEditable && (
                  <Button
                    size="small"
                    onClick={() => {
                      setIsMetadataUrlEditable(true);
                      setIsSaveEnabled((prev) => ({
                        ...prev,
                        [userRole]: true,
                      }));
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Col>

              <Col span={6}>
                <p className="search-label">API Key</p>
                <Input
                  size="small"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  disabled={isEditMode[userRole] && !isApiKeyEditable}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col
                span={2}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  padding: "0",
                  marginBottom: "10px",
                }}
              >
                {isEditMode.OKTA && !isApiKeyEditable && (
                  <Button
                    size="small"
                    onClick={() => {
                      setIsApiKeyEditable(true);
                      setIsSaveEnabled((prev) => ({
                        ...prev,
                        [userRole]: true,
                      }));
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Col>
            </Row>
          )}

          {/* ONELOGIN SECTION */}
          {userRole === "ONELOGIN" && (
            <>
              <Row gutter={16} style={{ marginTop: "1rem" }}>
                <Col span={6}>
                  <p className="search-label">SSO URL</p>
                  <Input
                    size="small"
                    value={oneloginSsoUrl}
                    onChange={(e) => setOneloginSsoUrl(e.target.value)}
                    disabled={isEditMode[userRole] && !isSsoUrlEditable}
                    style={{ width: "100%" }}
                  />
                </Col>
                <Col
                  span={2}
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    padding: "0",
                    marginBottom: "10px",
                  }}
                >
                  {isEditMode.ONELOGIN && !isSsoUrlEditable && (
                    <Button
                      size="small"
                      onClick={() => {
                        setIsSsoUrlEditable(true);
                        setIsSaveEnabled((prev) => ({
                          ...prev,
                          [userRole]: true,
                        }));
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Col>

                <Col span={6}>
                  <p className="search-label">SLO URL</p>
                  <Input
                    size="small"
                    value={sloUrl}
                    onChange={(e) => setSloUrl(e.target.value)}
                    disabled={isEditMode[userRole] && !isSloUrlEditable}
                    style={{ width: "100%" }}
                  />
                </Col>
                <Col
                  span={2}
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    padding: "0",
                    marginBottom: "10px",
                  }}
                >
                  {isEditMode.ONELOGIN && !isSloUrlEditable && (
                    <Button
                      size="small"
                      onClick={() => {
                        setIsSloUrlEditable(true);
                        setIsSaveEnabled((prev) => ({
                          ...prev,
                          [userRole]: true,
                        }));
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Col>

                <Col span={6}>
                  <p className="search-label">Metadata URL</p>
                  <Input
                    size="small"
                    value={oneloginMetadataUrl}
                    onChange={(e) => setOneloginMetadataUrl(e.target.value)}
                    disabled={isEditMode[userRole] && !isMetadataUrlEditable}
                    style={{ width: "100%" }}
                  />
                </Col>
                <Col
                  span={2}
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    padding: "0",
                    marginBottom: "10px",
                  }}
                >
                  {isEditMode.ONELOGIN && !isMetadataUrlEditable && (
                    <Button
                      size="small"
                      onClick={() => {
                        setIsMetadataUrlEditable(true);
                        setIsSaveEnabled((prev) => ({
                          ...prev,
                          [userRole]: true,
                        }));
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Col>
              </Row>
            </>
          )}

          {userRole !== "NORMAL" && (
            <Row style={{ marginTop: "1rem" }}>
              <Col>
                <Button
                  type="primary"
                  onClick={handleSave}
                  disabled={!isSaveEnabled[userRole]}
                >
                  Save Configuration
                </Button>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </>
  );
};
